import { MoreHorizontalIcon } from "lucide-react";

import { FilterSubMenuSwitch } from "@/components/FilterSubMenuSwitch";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useGetUserSettings } from "@/hooks/queries/useGetUserSettings";
import { useUpdateSetUserSettings } from "@/hooks/updateSets/useUpdateSetUserSettings";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { DashboardHideableColumn } from "@/types";

const columnLabels: Record<DashboardHideableColumn, string> = {
  [DashboardHideableColumn.LIQUIDITY]: "Liquidity",
  [DashboardHideableColumn.PRIORITY]: "Priority",
  [DashboardHideableColumn.APPS]: "Apps",
  [DashboardHideableColumn.COMMS_TO_SEND]: "Comms to Send",
  [DashboardHideableColumn.SUBMISSIONS]: "Submissions",
  [DashboardHideableColumn.DIFFICULTY]: "Difficulty",
  [DashboardHideableColumn.BOTTLENECK]: "Bottleneck",
  [DashboardHideableColumn.STAFFER]: "Staffer",
  [DashboardHideableColumn.CTD]: "CTD",
  [DashboardHideableColumn.CTD_REV]: "CTD Rev",
  [DashboardHideableColumn.FINALISTS]: "Finalists",
  [DashboardHideableColumn.PULSE]: "Pulse",
};

// The per-staffer view keeps its original smaller set; the All view can hide
// everything but Jobs. Both lists follow table column order.
const PERSONAL_VIEW_COLUMNS: DashboardHideableColumn[] = [
  DashboardHideableColumn.COMMS_TO_SEND,
  DashboardHideableColumn.SUBMISSIONS,
  DashboardHideableColumn.FINALISTS,
  DashboardHideableColumn.DIFFICULTY,
  DashboardHideableColumn.BOTTLENECK,
  DashboardHideableColumn.STAFFER,
  DashboardHideableColumn.CTD,
  DashboardHideableColumn.CTD_REV,
];
const ALL_VIEW_COLUMNS: DashboardHideableColumn[] = [
  DashboardHideableColumn.LIQUIDITY,
  DashboardHideableColumn.PRIORITY,
  DashboardHideableColumn.APPS,
  DashboardHideableColumn.COMMS_TO_SEND,
  DashboardHideableColumn.SUBMISSIONS,
  DashboardHideableColumn.FINALISTS,
  DashboardHideableColumn.DIFFICULTY,
  DashboardHideableColumn.BOTTLENECK,
  DashboardHideableColumn.STAFFER,
  DashboardHideableColumn.CTD,
  DashboardHideableColumn.CTD_REV,
  DashboardHideableColumn.PULSE,
];

export const DashboardColumnsPopover = ({
  isAllView,
}: {
  isAllView: boolean;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: userSettings } = useGetUserSettings();
  // Update
  const { mutate: updateUserSettings } = useUpdateSetUserSettings();

  /* Helpers */
  const hidden =
    (isAllView
      ? userSettings?.hiddenDashboardAllColumns
      : userSettings?.hiddenDashboardColumns) ?? [];
  const columns = isAllView ? ALL_VIEW_COLUMNS : PERSONAL_VIEW_COLUMNS;

  /* Handlers */
  const handleToggle = (column: DashboardHideableColumn) => {
    const isHidden = hidden.includes(column);
    const nextHidden = isHidden
      ? hidden.filter((c) => c !== column)
      : [...hidden, column];
    mixpanelUtils.trackButtonClick(
      "Toggle dashboard column",
      MIXPANEL_SCREENS.DASHBOARD,
      { column, hidden: !isHidden, all_view: isAllView },
    );
    updateUserSettings(
      isAllView
        ? { hiddenDashboardAllColumns: nextHidden }
        : { hiddenDashboardColumns: nextHidden },
    );
  };

  /* Render */
  const render = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-7 w-7 shrink-0 rounded-full p-0 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          size="icon"
          type="button"
          variant="ghost"
        >
          <MoreHorizontalIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px] p-0">
        <DropdownMenuLabel className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="mt-0" />
        {columns.map((column) => (
          <FilterSubMenuSwitch
            key={column}
            label={columnLabels[column]}
            checked={!hidden.includes(column)}
            onCheckedChange={() => {
              handleToggle(column);
            }}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return render();
};
